import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUserSafe } from "@/contexts/UserContext";

/**
 * useChatUnreadCount - Hook for tracking unread chat messages
 * 
 * Now uses UserContext instead of calling getUser() directly.
 */
export const useChatUnreadCount = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const userContext = useCurrentUserSafe();
  const userId = userContext?.user?.id;

  const fetchUnreadCount = useCallback(async (uid: string) => {
    try {
      const { data, error } = await supabase.rpc("get_total_unread_messages", {
        p_user_id: uid,
      });

      if (error) throw error;

      setUnreadCount(data || 0);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  }, []);

  // Load initial count when user is available
  useEffect(() => {
    if (!userId) {
      setUnreadCount(0);
      return;
    }

    fetchUnreadCount(userId);
  }, [userId, fetchUnreadCount]);

  // Set up realtime subscription with debouncing
  useEffect(() => {
    if (!userId) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    
    const debouncedFetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        fetchUnreadCount(userId);
      }, 300);
    };

    const channel = supabase
      .channel(`chat-updates-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages",
        },
        debouncedFetch
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "conversation_reads",
        },
        debouncedFetch
      )
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [userId, fetchUnreadCount]);

  return { 
    unreadCount, 
    refresh: () => userId && fetchUnreadCount(userId) 
  };
};
